import ApiHelper from "../../../common/utils/apiHelper";
import {
  buildAirlineMilesAndMoreBookingFields,
  readAirlineMilesAndMoreNumber
} from "../../../common/utils/transport/airlineBookingPayload";
import { type TransportAirlineApiContext } from "../../../common/utils/transport/airlineTransportContext";
import {
  buildDestinationTransportPayload,
  postDestinationTransportEstimation,
  DestinationTransportPayload
} from "../../../common/utils/transport/destinationTransportEstimation";
// actions
import { globalActionCreators, selectApiBaseUrl } from "../../../common/store/modules/globalModule";
import { confirmationActionCreators } from "../../../common/store/modules/bookingConfirmationModule";
// helpers
import { isEmailInvalid } from "../../../common/utils/helpers/isEmailInvalid";
import { checkForEmptyField } from "../../../common/utils/helpers/checkForEmptyFields";
import { extractFullNameParts } from "../../../common/utils/helpers/extractFullNameParts";
import { resolveLimos4ApiPaths } from "./helpers/resolveLimos4ApiPaths";

export const limos4ActionTypes = {
  LOAD_LIMOS4_INITIAL: "LOAD_LIMOS4_INITIAL",
  TOGGLE_LIMOS4_INITIAL_ERROR: "TOGGLE_LIMOS4_INITIAL_ERROR",
  SET_LIMOS4_SELECTED_OFFER: "SET_LIMOS4_SELECTED_OFFER",
  TOGGLE_LIMOS4_ESTIMATION_ERROR: "TOGGLE_LIMOS4_ESTIMATION_ERROR",
  TOGGLE_ESTIMATION_STATE_LIMOS4: "TOGGLE_ESTIMATION_STATE_LIMOS4",
  SET_LIMOS4_ESTIMATION: "SET_LIMOS4_ESTIMATION",
  SET_DATA_FOR_BOOKING: "SET_DATA_FOR_BOOKING",
  SET_BOOKING_RESPONSE_DATA: "SET_BOOKING_RESPONSE_DATA",
  TOGGLE_BOOKING_ERROR: "TOGGLE_BOOKING_ERROR",
  SET_BOOKING_ERROR_MESSAGE: "SET_BOOKING_ERROR_MESSAGE",
  TOGGLE_LIMOS4_PAYMENT_MODAL: "TOGGLE_LIMOS4_PAYMENT_MODAL",
  RESET_LIMOS4_STATE: "RESET_LIMOS4_STATE"
};

export const initialState = {
  limos4Initial: null,
  limos4SelectedOffer: null,
  limos4InitialError: false,
  limos4Estimation: null,
  limos4EstimationError: false,
  estimationAvailable: false,
  bookingResponseData: null,
  showPaymentModal: false,
  bookingError: false,
  bookingErrorMessage: [],
  dataForBooking: {
    commentForm: "",
    emailForm: "",
    nameForm: "",
    mobileNumberForm: ""
  }
};

const limos4Module = (state = initialState, action: any) => {
  switch (action.type) {
    case limos4ActionTypes.LOAD_LIMOS4_INITIAL:
      return {
        ...state,
        limos4Initial: action.limos4Initial
      };
    case limos4ActionTypes.SET_LIMOS4_SELECTED_OFFER:
      return {
        ...state,
        limos4SelectedOffer: action.limos4SelectedOffer
      };
    case limos4ActionTypes.TOGGLE_LIMOS4_INITIAL_ERROR:
      return {
        ...state,
        limos4InitialError: action.limos4InitialError
      };
    case limos4ActionTypes.SET_LIMOS4_ESTIMATION:
      return {
        ...state,
        limos4Estimation: action.limos4Estimation
      };
    case limos4ActionTypes.TOGGLE_LIMOS4_ESTIMATION_ERROR:
      return {
        ...state,
        limos4EstimationError: action.limos4EstimationError
      };
    case limos4ActionTypes.TOGGLE_ESTIMATION_STATE_LIMOS4:
      return {
        ...state,
        estimationAvailable: action.estimationAvailable
      };
    case limos4ActionTypes.SET_DATA_FOR_BOOKING:
      return {
        ...state,
        dataForBooking: action.dataForBooking
      };
    case limos4ActionTypes.SET_BOOKING_RESPONSE_DATA:
      return {
        ...state,
        bookingResponseData: action.bookingResponseData
      };
    case limos4ActionTypes.TOGGLE_LIMOS4_PAYMENT_MODAL:
      return {
        ...state,
        showPaymentModal: action.showPaymentModal
      };
    case limos4ActionTypes.TOGGLE_BOOKING_ERROR:
      return {
        ...state,
        bookingError: action.bookingError
      };
    case limos4ActionTypes.SET_BOOKING_ERROR_MESSAGE:
      return {
        ...state,
        bookingErrorMessage: action.bookingErrorMessage
      };
    case limos4ActionTypes.RESET_LIMOS4_STATE:
      return {
        ...state,
        ...initialState
      };
    default:
      return state;
  }
};

export default limos4Module;

// Toggle mock for Limos4 initial (estimation mocking lives in limos4Service)
const USE_MOCK_LIMOS4 = false;

const MOCK_LIMOS4_INITIAL_RESPONSE = {
  provider_title: "PRIVATE LIMOUSINE SERVICE",
  provider_description:
    "Limos4 is a <strong>global premium chauffeur and limousine service</strong> that was founded in Switzerland in 2010. The company offers tailor-made VIP transfers for business travellers and private individuals.",
  provider_logo: {
    url: "https://d1o0qt6ld1319s.cloudfront.net/dev/zurich/b65281cc-4ef1-415d-b815-8582aa8a7b8a/0cfc84c66895760.png",
    name: "limos4"
  },
  name: "Limo service",
  type: "limos4",
  main: null,
  items: [
    {
      cancellation_policy: "Cancellation Policy - Limos4 First Class Transfer - Transfer Hub",
      id: 1849,
      name: "Limos4 First Class Transfer test",
      type: "limos4",
      offer_type: null,
      code: "EBDDXVC",
      provider_name: "Limos4 test provider",
      provider_logo: {
        url: "https://d1o0qt6ld1319s.cloudfront.net/dev/zurich/b65281cc-4ef1-415d-b815-8582aa8a7b8a/0cfc84c66895760.png",
        name: "Limos4 test provider"
      },
      offer_image: {
        url: "https://d1o0qt6ld1319s.cloudfront.net/dev/event/b8855c16-85f3-4be0-a9bf-32247feae382/f2853c4001ae450.png",
        name: "limos4"
      },
      departure_address: null,
      destination_address: null,
      airport_pickup: false,
      booking_setting: {
        customer_name: "mandatory",
        customer_email: "mandatory",
        customer_phone: "hidden",
        comment: "shown",
        birthdate: "hidden"
      },
      time_limit: null
    },
    {
      cancellation_policy: null,
      id: 1856,
      name: "Limos4 Business Class Transfer",
      type: "limos4",
      offer_type: null,
      code: "EGQDCZS",
      provider_name: "Limos4 test provider",
      provider_logo: {
        url: "https://d1o0qt6ld1319s.cloudfront.net/dev/zurich/b65281cc-4ef1-415d-b815-8582aa8a7b8a/0cfc84c66895760.png",
        name: "Limos4 test provider"
      },
      offer_image: {
        url: "https://d1o0qt6ld1319s.cloudfront.net/dev/event/b8855c16-85f3-4be0-a9bf-32247feae382/8ba24ee38cbcb51de0fc28c4ae0a306d.png",
        name: "limos4"
      },
      departure_address: null,
      destination_address: null,
      airport_pickup: false,
      booking_setting: {
        customer_name: "mandatory",
        customer_email: "mandatory",
        customer_phone: "hidden",
        comment: "shown",
        birthdate: "hidden"
      },
      time_limit: null
    },
    {
      cancellation_policy: "Test",
      id: 1856,
      name: "Limos4 Test",
      type: "limos4",
      offer_type: null,
      code: "EGQDCZS123",
      provider_name: "Limos4 test provider",
      provider_logo: {
        url: "https://d1o0qt6ld1319s.cloudfront.net/dev/zurich/b65281cc-4ef1-415d-b815-8582aa8a7b8a/0cfc84c66895760.png",
        name: "Limos4 test provider"
      },
      offer_image: {
        url: "https://d1o0qt6ld1319s.cloudfront.net/dev/event/b8855c16-85f3-4be0-a9bf-32247feae382/8ba24ee38cbcb51de0fc28c4ae0a306d.png",
        name: "limos4"
      },
      departure_address: null,
      destination_address: null,
      airport_pickup: false,
      booking_setting: {
        customer_name: "mandatory",
        customer_email: "mandatory",
        customer_phone: "hidden",
        comment: "shown",
        birthdate: "hidden"
      },
      time_limit: null
    }
  ],
  details: null
};

export type Limos4CalculationPayload = {
  time: string;
  date: string;
  departureAddress: string | null;
  departureAddressId: string | null;
  destinationAddress: string | null;
  destinationAddressId: string | null;
  attributes: any[];
  offerCode: string;
  details: {
    luggageCount: number;
    passengerCount: number;
  };
};

export const limos4ActionCreators = {
  resetLimos4StateAction: () => (dispatch: any) => {
    dispatch({ type: limos4ActionTypes.RESET_LIMOS4_STATE });
  },

  getLimos4InitialAction: (transportContext: TransportAirlineApiContext) => async (dispatch: any, getState: any) => {
    const apiUrl = selectApiBaseUrl(getState());
    const paths = resolveLimos4ApiPaths(transportContext);

    dispatch(globalActionCreators?.toggleGlobalLoaderAction(true));
    dispatch({
      type: limos4ActionTypes.TOGGLE_LIMOS4_INITIAL_ERROR,
      limos4InitialError: false
    });

    if (paths.kind === "invalid_airline") {
      dispatch({
        type: limos4ActionTypes.TOGGLE_LIMOS4_INITIAL_ERROR,
        limos4InitialError: true
      });
      dispatch(globalActionCreators?.toggleGlobalLoaderAction(false));
      return;
    }

    try {
      let data: any;
      if (USE_MOCK_LIMOS4) {
        await new Promise(resolve => setTimeout(resolve, 250));
        data = MOCK_LIMOS4_INITIAL_RESPONSE;
      } else {
        const res = await ApiHelper.get(`${apiUrl}/${paths.mainPath}`);
        // Catalog load is synchronous HTTP (guest + airline). WebSocket follows POST estimation only.
        data = res?.data;
      }

      const items = data?.items ?? [];
      const currentCode = getState().limos4.limos4SelectedOffer?.code;
      const limos4SelectedOffer =
        items.find((item: { code?: string }) => item?.code === currentCode) ?? items[0] ?? null;

      dispatch({
        type: limos4ActionTypes.LOAD_LIMOS4_INITIAL,
        limos4Initial: data
      });
      dispatch({
        type: limos4ActionTypes.SET_LIMOS4_SELECTED_OFFER,
        limos4SelectedOffer
      });
    } catch (_err: any) {
      dispatch({
        type: limos4ActionTypes.TOGGLE_LIMOS4_INITIAL_ERROR,
        limos4InitialError: true
      });
    } finally {
      dispatch(globalActionCreators?.toggleGlobalLoaderAction(false));
    }
  },

  setLimos4SelectedOfferAction: (limos4SelectedOffer: any) => (dispatch: any) => {
    dispatch({
      type: limos4ActionTypes.SET_LIMOS4_SELECTED_OFFER,
      limos4SelectedOffer
    });
  },

  toggleEstimationAvailableStateAction: (status: boolean) => (dispatch: any) => {
    dispatch({
      type: limos4ActionTypes.TOGGLE_ESTIMATION_STATE_LIMOS4,
      estimationAvailable: status
    });
  },

  toggleLimos4EstimationErrorAction: (status: boolean) => (dispatch: any) => {
    dispatch({
      type: limos4ActionTypes.TOGGLE_LIMOS4_ESTIMATION_ERROR,
      limos4EstimationError: status
    });
  },

  setLimos4EstimationAction: (limos4Estimation: any) => (dispatch: any) => {
    dispatch({
      type: limos4ActionTypes.SET_LIMOS4_ESTIMATION,
      limos4Estimation
    });
  },

  getLimos4EstimationAction:
    (
      params: Limos4CalculationPayload,
      offerCode: string | null | undefined,
      transportContext: TransportAirlineApiContext
    ) =>
    async (dispatch: any, getState: any) => {
      dispatch(limos4ActionCreators.toggleEstimationAvailableStateAction(false));
      dispatch(limos4ActionCreators.toggleLimos4EstimationErrorAction(false));
      dispatch(globalActionCreators?.toggleGlobalLoaderAction(true));

      try {
        const apiUrl = selectApiBaseUrl(getState()) as string;

        const basePayload: DestinationTransportPayload = buildDestinationTransportPayload({
          time: params?.time,
          date: params?.date,
          departureAddress: params?.departureAddress ?? null,
          departureAddressId: params?.departureAddressId ?? null,
          destinationAddress: params?.destinationAddress ?? null,
          destinationAddressId: params?.destinationAddressId ?? null,
          milesAndMore: readAirlineMilesAndMoreNumber(),
          offerCode: params?.offerCode ?? offerCode,
          details: params?.details ?? undefined
        });

        const data = await postDestinationTransportEstimation({
          apiUrl,
          transportContext,
          payload: basePayload,
          v2: { type: "limos4" },
          airline: { path: iataCode => `v1/airports/${encodeURIComponent(iataCode)}/transports/limos4` }
        });

        dispatch(limos4ActionCreators.setLimos4EstimationAction(data));
        dispatch(limos4ActionCreators.toggleEstimationAvailableStateAction(true));
        return data;
      } catch (_err: any) {
        dispatch(limos4ActionCreators.toggleLimos4EstimationErrorAction(true));
        dispatch(limos4ActionCreators.toggleEstimationAvailableStateAction(false));
      } finally {
        dispatch(globalActionCreators?.toggleGlobalLoaderAction(false));
      }
    },

  updateFormForBookingAction:
    (dataForBooking: { commentForm: string; emailForm: string; nameForm: string; mobileNumberForm: string }) =>
    (dispatch: any) => {
      dispatch({
        type: limos4ActionTypes.SET_DATA_FOR_BOOKING,
        dataForBooking
      });
    },

  toggleBookingErrorAction: (bookingError: boolean) => (dispatch: any) => {
    dispatch({
      type: limos4ActionTypes.TOGGLE_BOOKING_ERROR,
      bookingError
    });
  },

  setBookingErrorMessageAction: (message: any) => (dispatch: any) => {
    dispatch({
      type: limos4ActionTypes.SET_BOOKING_ERROR_MESSAGE,
      bookingErrorMessage: message
    });
  },

  togglePaymentModalAction: (showPaymentModal: boolean) => (dispatch: any) => {
    dispatch({
      type: limos4ActionTypes.TOGGLE_LIMOS4_PAYMENT_MODAL,
      showPaymentModal
    });
  },

  buyTicketAction:
    (params: any, navigate: any, transportContext: TransportAirlineApiContext) =>
    async (dispatch: any, getState: any) => {
      const dataForBooking = getState().limos4.dataForBooking;
      const limos4SelectedOffer = getState().limos4.limos4SelectedOffer;
      const limos4Estimation = getState().limos4.limos4Estimation;
      const paths = resolveLimos4ApiPaths(transportContext);

      dispatch(limos4ActionCreators?.toggleBookingErrorAction(false));

      let errorField = {
        emailForm: false,
        commentForm: false,
        nameForm: false,
        mobileNumberForm: false
      };

      let errorCheck = true;

      if (
        isEmailInvalid(dataForBooking.emailForm) ||
        (checkForEmptyField(dataForBooking.emailForm) &&
          limos4SelectedOffer?.booking_setting?.customer_email === "mandatory")
      ) {
        errorField.emailForm = true;
        errorCheck = false;
      }

      if (
        (limos4SelectedOffer?.booking_setting?.comment === "mandatory" &&
          checkForEmptyField(dataForBooking.commentForm)) ||
        (dataForBooking?.commentForm !== "" && dataForBooking?.commentForm?.length < 3)
      ) {
        errorField.commentForm = true;
        errorCheck = false;
      }

      if (
        limos4SelectedOffer?.booking_setting?.customer_name === "mandatory" &&
        checkForEmptyField(dataForBooking.nameForm)
      ) {
        errorField.nameForm = true;
        errorCheck = false;
      }

      if (
        limos4SelectedOffer?.booking_setting?.customer_phone === "mandatory" &&
        checkForEmptyField(dataForBooking.mobileNumberForm)
      ) {
        errorField.mobileNumberForm = true;
        errorCheck = false;
      }

      if (!errorCheck) {
        dispatch({ type: "TOGGLE_ERROR_FIELD", errorExists: true, errorField: errorField });
        return;
      }

      dispatch({ type: "TOGGLE_ERROR_FIELD", errorExists: false, errorField: errorField });
      dispatch(globalActionCreators?.toggleGlobalLoaderAction(true));

      // reset booking error message
      dispatch(limos4ActionCreators.setBookingErrorMessageAction([]));

      // remove bookingResponseDataStorage from the storage, before setting it again
      localStorage.removeItem("bookingResponseDataStorage");

      const language = localStorage.getItem("i18nextLng") || "en";

      try {
        if (paths.kind === "invalid_airline") {
          dispatch(limos4ActionCreators?.toggleBookingErrorAction(true));
          return;
        }

        const apiUrl = selectApiBaseUrl(getState()) as string;
        const passengerCount = Number(params?.details?.passengerCount);
        const apiParams = {
          summary: {
            start_date: params?.start_date,
            adult: Number.isFinite(passengerCount) && passengerCount > 0 ? passengerCount : 1,
            timeslot: params?.timeslot,
            offer_code: params?.offer_code ?? null
          },
          personal_information: {
            first_name: extractFullNameParts(dataForBooking.nameForm)?.firstName ?? null,
            last_name: extractFullNameParts(dataForBooking.nameForm)?.lastName ?? null,
            email: dataForBooking.emailForm,
            phone: dataForBooking.mobileNumberForm,
            comment: dataForBooking.commentForm ? dataForBooking.commentForm : null
          },
          ...buildAirlineMilesAndMoreBookingFields(transportContext),
          hotel_stay_code: getState().tenant.tenantInfo?.code,
          extraBookingData: {
            sendBookingConfirmationCopy: true,
            origin_address: params?.departureAddress,
            origin_address_id: params?.departureAddressId ? params?.departureAddressId : null,
            destination_address: params?.destinationAddress,
            destination_address_id: params?.destinationAddressId ? params?.destinationAddressId : null,
            quote_id: limos4Estimation?.details?.quote_id,
            attributes: []
          }
        };

        let responseData: any;

        const response = await ApiHelper.post(`${apiUrl}/${paths.bookingsPath}`, {}, apiParams);
        responseData = response?.data;

        localStorage.setItem("bookingResponseDataStorage", JSON.stringify(responseData));

        // We can get API status 200, with booking_status=error
        if (responseData?.booking_status === "error") {
          dispatch(limos4ActionCreators?.toggleBookingErrorAction(true));
          return;
        }

        dispatch(limos4ActionCreators?.toggleBookingErrorAction(false));
        dispatch({
          type: limos4ActionTypes.SET_BOOKING_RESPONSE_DATA,
          bookingResponseData: responseData
        });

        // if not paid, show PaymentModal; otherwise redirect to confirmation.
        if (responseData?.payment_status === "paid") {
          await dispatch(confirmationActionCreators?.getBookingStatusAction());
          await navigate(`/${language}/transport/limos4/confirmation`, { replace: true });
        } else {
          dispatch(limos4ActionCreators?.togglePaymentModalAction(true));
        }
      } catch (err: any) {
        if (err?.response?.data?.fields) {
          dispatch(limos4ActionCreators.setBookingErrorMessageAction(err?.response?.data?.fields));
        }
        dispatch(limos4ActionCreators?.toggleBookingErrorAction(true));
      } finally {
        dispatch(globalActionCreators?.toggleGlobalLoaderAction(false));
      }
    }
};
